import { useEffect, useState } from 'react';

/**
 * ─── Home section publisher ──────────────────────────────────────────────────
 *
 * Tiny module-level store for the currently-visible home section index.
 *
 *   null   → home route is not mounted (or has unmounted)
 *   0      → hero / landing
 *   1..3   → projects / lab / info
 *
 * Consumers outside the Home tree (e.g. the global AudioToggle that lives in
 * App.js) subscribe via useHomeSection().  Home pushes updates via
 * setHomeSection() in its mount/update/unmount lifecycle.
 *
 * Deliberately *not* React context — AudioToggle is mounted as a sibling of
 * the route Switch, so the Home component cannot supply it via context
 * without lifting state into App.  A pub/sub keeps the wiring local.
 * ──────────────────────────────────────────────────────────────────────────── */

let currentSection = null;
const listeners = new Set();

export function setHomeSection(section) {
    if (currentSection === section) return;
    currentSection = section;
    listeners.forEach(fn => fn(currentSection));
}

export function getHomeSection() {
    return currentSection;
}

/**
 * Plain (non-React) subscription for consumers like the WebGL background that
 * live outside the component tree. Returns an unsubscribe function.
 */
export function subscribeHomeSection(fn) {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
}

export function useHomeSection() {
    const [section, setSection] = useState(currentSection);
    useEffect(() => {
        // Resync once on subscribe in case the value changed between render and effect.
        setSection(currentSection);
        const handler = (s) => setSection(s);
        listeners.add(handler);
        return () => { listeners.delete(handler); };
    }, []);
    return section;
}
